import React, { useState } from "react";
import { Link } from "react-router-dom";

/* PACKAGES */
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import { PopupModal } from "react-calendly";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* CONFIG */
import { animation, containerAnimation } from "config";

/* ICONS */
import { MdOutlineArrowOutward } from "react-icons/md";
import { BsCalendar2DateFill } from "react-icons/bs";

/* FORM VALIDATIONS */
import * as yup from "yup";
import { useFormik } from "formik";

/* API */
import { newsletterAPI } from "service/api";

/* VALIDATION SCHEMA */
const waitlistValidation = yup.object().shape({
  email: yup
    .string()
    .email()
    .min(1, "Email Address is required")
    .required("Email Address is required."),
});

/* TAGS */
const tags = [
  {
    id: 1,
    name: "Task Management",
    link: "/blogs/task-management",
  },
  {
    id: 2,
    name: "Employee Engagement",
    link: "/blogs/employee-engagement",
  },
  {
    id: 3,
    name: "Performance Management",
    link: "/blogs/performance-management",
  },
  {
    id: 4,
    name: "Change Management",
    link: "/blogs/change-management",
  },
  {
    id: 5,
    name: "OKR Management",
    link: "/blogs/okr-management",
  },
  {
    id: 6,
    name: "Project Planning",
    link: "/blogs/project-planning",
  },
  {
    id: 7,
    name: "Project Management",
    link: "/blogs/project-management",
  },
  {
    id: 8,
    name: "Team collaboration",
    link: "/blogs/team-collaboration",
  },
];

const WhyEmployees = () => {
  const [calendlyModal, setCalendlyModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  /* FORM HANDLING */
  const formHandling = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: waitlistValidation,
    onSubmit: (values) => {
      const obj = {
        email: values.email,
      };
      setFormLoading(true);
      newsletterAPI(obj)
        .then((res) => {
          if (res?.status) {
            toast.success(
              "Congratulations! 🎉 You have subscribed to our newsletter.",
            );
          } else {
            toast.error(
              "We were not able to capture your details. Please try again.",
            );
          }
          setFormLoading(false);
        })
        .catch((e) => {
          setFormLoading(false);
          toast.error(e?.response?.data?.message);
        })
        .finally((res) => {
          formHandling.resetForm();
          setFormLoading(false);
        });
    },
  });

  /* SMALL TAGS BUTTON */
  const SmallTagsButton = (props) => {
    return (
      <Link to={props.link}>
        <Button className="shadow-none hover:shadow-none bg-greyBg hover:bg-logoOrange rounded-md text-sm font-asap font-normal text-greyText hover:text-white normal-case px-4 py-2 transition-all duration-300 ease-in-out">
          {props.label}
        </Button>
      </Link>
    );
  };

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-transparent"
    >
      {/* TOAST CONTAINER */}
      <ToastContainer theme="colored" closeOnClick autoClose={5000} />

      {/* CONTENT */}
      <motion.div variants={animation} className="py-5 xl:py-12 lg:py-12">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3">
            {/* CONTENT */}
            <div className="col-span-2 border-r-0 xl:border-r lg:border-r border-greyBorder">
              <div className="pr-0 xl:pr-8 lg:pr-8">
                <h1 className="mt-5 text-xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  The Invisible Majority: Why Most Employees Feel Unseen 364
                  Days a Year and Judged on the 365th
                </h1>

                <div className="mt-5">
                  <p className="text-lg font-asap font-normal text-greyText leading-relaxed">
                    In workplaces around the world, a quiet crisis plays out
                    every single day.
                  </p>
                  <p className="mt-3 text-lg font-asap font-normal text-greyText leading-relaxed">
                    Talented employees show up. They solve problems, support
                    teammates, manage conflicts, meet deadlines, and keep the
                    wheels turning. Yet for most of the year, their efforts go
                    unnoticed only to be intensely scrutinized during one
                    high-stakes moment: performance review day.
                  </p>
                  <p className="mt-3 text-lg font-asap font-normal text-greyText leading-relaxed">
                    For many professionals, work feels like 364 days of
                    invisibility followed by one day of judgment.
                  </p>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Hidden Crisis of Employee Invisibility
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Research reveals a troubling reality:{" "}
                      <span className="font-asap font-bold text-black">
                        nearly half of employees (46–48%) believe leadership
                        fundamentally misunderstands or undervalues their
                        contributions
                      </span>
                      . This gap isn’t about ego, it's about awareness.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      When people feel unseen, engagement erodes. Motivation
                      drops. Innovation slows. Over time, employees begin
                      questioning their value and their future within the
                      organization.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Recognition often arrives once a year if at all. That long
                      silence sends a clear message:{" "}
                      <span className="italic">
                        what you do every day doesn’t really matter.
                      </span>
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      In extreme cases, this invisibility becomes symbolic of
                      deeper organizational failures. Stories of employees going
                      unnoticed for days during emergencies are shocking but for
                      many workers, emotional invisibility is a daily reality.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Real Cost of Being Invisible
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Employee invisibility isn’t just a morale issue—it’s a
                      business problem.
                    </p>
                    <ul className="ml-8 mt-3 list-disc space-y-2 text-lg font-asap font-bold text-black leading-relaxed">
                      <li>
                        Nearly 30% of workers report feeling invisible at work
                      </li>
                      <li>
                        44% say they’ve been passed over for raises or
                        promotions due to lack of recognition
                      </li>
                      <li>
                        Work quality and motivation decline when effort feels
                        ignored
                      </li>
                    </ul>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      When organizations fail to recognize contributions
                      accurately, they misallocate talent. High performers
                      stagnate. Potential leaders disengage. The company pays
                      the price in lost energy, lost ideas, and lost people.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The “Invisible Work” No One Talks About
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Some of the most valuable work in organizations rarely
                      shows up on dashboards:
                    </p>
                    <ul className="ml-8 mt-3 list-disc space-y-2 text-lg font-asap font-normal text-greyText leading-relaxed">
                      <li>Emotional labor</li>
                      <li>Mentoring and coaching</li>
                      <li>Conflict resolution</li>
                      <li>Knowledge sharing</li>
                      <li>Holding teams together during change</li>
                    </ul>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      This “shadow work” keeps organizations functioning but
                      it’s often ignored because it’s difficult to measure.
                      Women and minority employees disproportionately carry this
                      burden, creating systemic barriers to recognition and
                      advancement.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      When invisible work goes unrewarded, employees adapt. They
                      stop volunteering ideas. They stop taking risks. They do
                      only what’s required and nothing more.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Paradox of Surveillance and Invisibility
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Ironically, many workplaces track more data than ever
                      before.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Keystrokes. Activity logs. Productivity dashboards.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Yet despite this constant monitoring, employees report
                      feeling less seen as human beings.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Surveillance captures output but misses context, effort,
                      creativity, and care. When engagement dips, organizations
                      often respond by increasing monitoring, which only deepens
                      distrust. Employees comply but emotionally withdraw.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      You can measure everything someone does and still
                      understand nothing about who they are.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      Why Remote Work Made the Problem Worse
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Remote and hybrid work environments have amplified
                      invisibility.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Studies show that remote employees can go{" "}
                      <span className="font-asap font-bold text-black">
                        six or more days without speaking to their manager
                      </span>
                      . Visibility now requires extra effort documenting
                      achievements, scheduling calls, and self-promoting in ways
                      that feel unnatural to many people.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Once again, the burden isn’t shared equally. Women, in
                      particular, face steeper challenges as informal
                      relationship-building disappears. Without intentional
                      communication, distance becomes disconnection.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      Making Every Day Count: How Leaders Can Close the Gap
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      The problem isn’t employees, it's outdated leadership
                      practices.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Annual performance reviews create the very issue they aim
                      to solve: long stretches of silence followed by sudden
                      judgment. Forward-thinking organizations are replacing
                      this model with continuous recognition.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Here’s what actually works:
                    </p>
                    <ul className="ml-8 mt-3 list-disc space-y-2 text-lg font-asap font-normal text-greyText leading-relaxed">
                      <li>
                        <span className="font-asap font-bold text-black">
                          Regular 1:1 Check-Ins:
                        </span>{" "}
                        Weekly or bi-weekly conversations focused on real work
                        not just metrics build trust and prevent surprises.
                      </li>
                      <li>
                        <span className="font-asap font-bold text-black">
                          Specific, Authentic Recognition:
                        </span>{" "}
                        Generic praise isn’t enough. Timely acknowledgment of
                        both visible wins and invisible contributions matters.
                      </li>
                      <li>
                        <span className="font-asap font-bold text-black">
                          Valuing Invisible Work:
                        </span>{" "}
                        Empathy, mentoring, and collaboration must be recognized
                        and rewarded not treated as “extra.”
                      </li>
                      <li>
                        <span className="font-asap font-bold text-black">
                          Investing in Leadership Skills:
                        </span>{" "}
                        Managers need training in observation, empathy, and
                        communication, not just performance tracking.
                      </li>
                    </ul>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Organizations that close the perception gap see higher
                      retention, stronger engagement, and more innovation. When
                      people feel genuinely seen, they bring their full selves
                      to work.
                    </p>
                  </div>

                  <div className="mt-10">
                    <h2 className="text-lg xl:text-xl lg:text-xl font-asap font-bold text-black">
                      The Future of Work Must Be Human
                    </h2>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      The most successful organizations won’t be those with the
                      best surveillance tools but those with the strongest human
                      connections.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Recognition shouldn’t happen once a year. It should happen
                      every day.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      Because when employees are seen consistently not just
                      evaluated occasionally they don’t just perform better.
                      They thrive.
                    </p>
                    <p className="text-lg font-asap font-normal text-greyText mt-3 leading-relaxed">
                      And the invisible majority? They’ve always had the
                      potential. They were just waiting to be noticed.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="col-span-1 mt-5 xl:mt-0 lg:mt-0">
              <div className="pl-0 xl:pl-8 lg:pl-8  h-fit sticky top-24">
                {/* NEWSLETTER */}
                <motion.div
                  variants={animation}
                  className="pb-8 border-b border-greyBorder"
                >
                  <div
                    variants={animation}
                    className="bg-greyBg bg-opacity-50 border border-greyBorder rounded-lg p-5 xl:p-8 lg:p-8"
                  >
                    <h5 className="text-2xl font-asap font-bold">
                      Join Our Community
                    </h5>
                    <p className="text-lg font-asap font-normal text-greyText mt-3">
                      Join us so that we can send you updates on new features,
                      valuable tips, expert strategies and our exclusive offers
                      delivered directly to your inbox.
                    </p>
                    <form className="mt-5" onSubmit={formHandling.handleSubmit}>
                      <input
                        type="email"
                        placeholder="Type you email address"
                        name="newsletterEmail"
                        id="newsletterEmail"
                        value={formHandling.values.email}
                        onChange={formHandling.handleChange}
                        className={`w-full block bg-white px-4 py-3 rounded-lg text-sm font-asap font-bold text-black border ${
                          formHandling.errors.email &&
                          formHandling.touched.email
                            ? "border-red-500"
                            : "border-greyBorder"
                        } border-greyBorder focus:outline-brandColor placeholder:font-asap font-normal`}
                      />
                      <Button
                        disabled={formLoading}
                        type={formLoading ? "button" : "submit"}
                        className="group mt-3 w-full flex items-center gap-1 justify-center shadow-none hover:shadow-none font-asap font-bold text-white rounded-md bg-brandColor normal-case text-sm hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                      >
                        {formLoading ? "Please wait.." : "Subscribe"}
                        <MdOutlineArrowOutward className="w-4 h-4 text-white" />
                      </Button>
                    </form>
                  </div>
                </motion.div>

                {/* Schedule a Demo */}
                <motion.div variants={animation} className="pt-8">
                  <BsCalendar2DateFill className="w-10 h-10 text-black" />
                  <h5 className="mt-3 text-2xl font-asap font-bold leading-snug">
                    Learn how Aligned Rewards can be useful for your
                    organization
                  </h5>
                  <p className="mt-3 text-lg font-asap font-normal text-greyText">
                    Experience a guided walkthrough of all our features in this
                    exclusive video!
                  </p>
                  <Button
                    className="mt-5 shadow-none hover:shadow-none bg-brandColor text-white font-asap font-bold text-sm normal-case"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Schedule a Demo
                  </Button>
                </motion.div>

                {/* TAGS */}
                <motion.div variants={animation} className="pt-8 hidden">
                  <h5 className="text-xl xl:text-2xl lg:text-2xl font-asap font-bold text-black tracking-tight">
                    Tags
                  </h5>
                  <div className="flex items-center flex-wrap gap-2 mt-5">
                    {tags.map((item) => (
                      <React.Fragment key={item.id}>
                        <SmallTagsButton label={item.name} link={item.link} />
                      </React.Fragment>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CALENDLY MODAL */}
      <PopupModal
        open={calendlyModal}
        onModalClose={() => setCalendlyModal(!calendlyModal)}
        rootElement={document.getElementById("root")}
        url="https://calendly.com/contact-ucp8/30min"
      />
    </motion.section>
  );
};

export default WhyEmployees;
